import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  CircularProgress,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Tooltip,
  Alert,
  Chip,
  Avatar,
  Checkbox,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  YouTube as YouTubeIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ThumbDown as ThumbDownIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { alpha, useTheme, type Theme } from "@mui/material/styles";
import { DurationHmsInput } from "./DurationHmsInput";
import BrandStringListEditor from "./BrandStringListEditor";
import { useDebounce } from "../utils/useDebounce";
import { setReviewDialogOpen } from "./ReviewTimeTracker";
import {
  youtubeThumbnailUrlFromWatchUrl,
  youtubeWatchUrlFromWatchUrl,
} from "../utils/youtubeVideoId";
import {
  VISUAL_CUE_OPTIONS,
  type VisualCue,
} from "../utils/visualCueLabels";
import {
  findTranscriptSearchMatches,
  findTranscriptSegmentIndexForTime,
  formatMentionTime,
  formatTranscriptSegmentLabel,
  renderSegmentTranscriptText,
} from "../utils/mentionTranscriptDisplay";
import {
  nudgeManagedYoutubeTime,
  seekYoutubeViaExtension,
  toggleManagedYoutubePlayback,
  toggleManagedYoutubeMute,
} from "../utils/youtubeExtension";
import apiService from "../services/api";
import { BrandMention } from "../types";
import { DEFAULT_REJECT_REASON, REJECT_MENTION_REASON_PRESETS } from "../utils/rejectMentionReasonPresets";
import { getBrandLogoProps, resolveBrandLogoFromMentions } from "../utils/brandLogoUtils";

const subtleMutedSurfaceBg = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.06)
    : theme.palette.grey[50];

const BRAND_SEARCH_DEBOUNCE_MS = 600;

const IS_APPLE_PLATFORM =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPod|iPad/i.test(navigator.platform);

function formatModShortcut(key: string, opts?: { shift?: boolean }): string {
  if (IS_APPLE_PLATFORM) {
    const mod = "⌘";
    const shift = opts?.shift ? "⇧" : "";
    if (key === "↩") return `${mod}${shift}↩`;
    return `${mod}${shift}${key.length === 1 ? key.toUpperCase() : key}`;
  }
  if (key === "↩") return opts?.shift ? "Ctrl+Shift+Enter" : "Ctrl+Enter";
  if (key === "→" || key === "←") {
    return opts?.shift ? `Ctrl+Shift+${key}` : `Ctrl+${key}`;
  }
  return opts?.shift ? `Ctrl+Shift+${key.toUpperCase()}` : `Ctrl+${key.toUpperCase()}`;
}

function formatModShiftBackspaceShortcut(): string {
  return IS_APPLE_PLATFORM ? "⌘⇧⌫" : "Ctrl+Shift+Backspace";
}

function formatModBackspaceShortcut(): string {
  return IS_APPLE_PLATFORM ? "⌘⌫" : "Ctrl+Backspace";
}

function formatModEnterShortcut(): string {
  return formatModShortcut("↩");
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable;
}

function isActivationTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.closest(
      "button, a[href], [role='button'], [role='checkbox'], [role='switch'], [role='tab'], [role='option'], [role='menuitem']",
    ) != null
  );
}

const ButtonShortcutLabel: React.FC<{ shortcut: string }> = ({ shortcut }) => (
  <Box
    component="span"
    sx={{
      display: { xs: "none", lg: "inline" },
      ml: 0.75,
      px: 0.5,
      py: 0.125,
      borderRadius: 0.5,
      fontSize: "0.65rem",
      lineHeight: 1.4,
      fontWeight: 600,
      letterSpacing: "0.02em",
      bgcolor: "action.selected",
      color: "text.secondary",
    }}
  >
    {shortcut}
  </Box>
);

function rankBrandSearchMatch(
  brand: { name: string; aliases?: string[] },
  query: string,
): number {
  const q = query.toLowerCase();
  if (!q) return 0;

  const name = brand.name.toLowerCase();
  if (name === q) return 0;
  if (brand.aliases?.some((alias) => alias.toLowerCase() === q)) return 1;
  if (name.startsWith(q)) return 2;
  if (brand.aliases?.some((alias) => alias.toLowerCase().startsWith(q))) return 3;
  if (name.includes(q)) return 4;
  if (brand.aliases?.some((alias) => alias.toLowerCase().includes(q))) return 5;
  return 6;
}

function sortBrandsForSearch<T extends { name: string; aliases?: string[] }>(
  brands: T[],
  query: string,
): T[] {
  const trimmed = query.trim();
  if (!trimmed) return brands;

  return [...brands].sort((a, b) => {
    const rankDiff =
      rankBrandSearchMatch(a, trimmed) - rankBrandSearchMatch(b, trimmed);
    if (rankDiff !== 0) return rankDiff;
    return a.name.localeCompare(b.name);
  });
}

export type MentionReviewSession = {
  contentId: string;
  contentUrl?: string;
  contentTitle: string;
  mentions: BrandMention[];
  initialIndex: number;
  initialMentionId?: string;
  reviewReadyAt?: string;
  programId?: string;
  programName?: string;
};

export function isRejectedMention(mention: BrandMention): boolean {
  return mention.feedback?.status === "rejected";
}

export function getActiveReviewMentions(mentions: BrandMention[]): BrandMention[] {
  return mentions.filter((m) => !isRejectedMention(m));
}

export function indexOfReviewMention(
  mentions: BrandMention[],
  mentionId: string,
): number {
  return mentions.findIndex((m) => m._id === mentionId);
}

type PlacementType = NonNullable<BrandMention["placementType"]>;

export type MentionSaveSource = "manual" | "auto";

export type MentionReviewFormHandle = {
  saveIfDirty: () => Promise<{ mention: boolean; aliases: boolean }>;
};

const PLACEMENT_OPTIONS: { value: PlacementType; label: string; color: string }[] = [
  { value: "content", label: "Content", color: "#42a5f5" },
  { value: "opening_credits", label: "Opening credits", color: "#ab47bc" },
  { value: "mid_credits", label: "Mid credits", color: "#26c6da" },
  { value: "closing_credits", label: "Closing credits", color: "#ec407a" },
  { value: "physical_placement", label: "Physical placement", color: "#ff9800" },
  { value: "banner", label: "Banner", color: "#66bb6a" },
];

function placementTypeColor(placementType?: BrandMention["placementType"]): string {
  if (placementType === "overlay") return "#ffee58";
  return (
    PLACEMENT_OPTIONS.find((option) => option.value === placementType)?.color ??
    PLACEMENT_OPTIONS[0].color
  );
}

function placementTypeLabel(placementType?: BrandMention["placementType"]): string {
  return (
    PLACEMENT_OPTIONS.find((o) => o.value === placementType)?.label ?? "Content"
  );
}

function mentionStartSec(mention: BrandMention): number {
  const withSegment = mention as BrandMention & { segmentStart?: number };
  return Number(mention.startTime ?? withSegment.segmentStart ?? 0);
}

export function sortMentionsByStartTime(mentions: BrandMention[]): BrandMention[] {
  return [...mentions].sort(
    (a, b) =>
      mentionStartSec(a) - mentionStartSec(b) ||
      a._id.localeCompare(b._id),
  );
}

type MentionTimelineVariant = "rail" | "strip";

type MentionTimelineSidebarProps = {
  mentions: BrandMention[];
  currentMentionId: string;
  selectionEnabled: boolean;
  onSelectionEnabledChange: (enabled: boolean) => void;
  selectedIds: ReadonlySet<string>;
  onToggleSelected: (mentionId: string) => void;
  onToggleAll: () => void;
  onSelect: (mentionId: string) => void;
  disabled?: boolean;
  variant?: MentionTimelineVariant;
};

type MentionTimelineItemProps = {
  mention: BrandMention;
  allMentions: BrandMention[];
  isActive: boolean;
  selectionEnabled: boolean;
  selected: boolean;
  disabled?: boolean;
  onToggleSelected: (mentionId: string) => void;
  onSelect: (mentionId: string) => void;
  activeItemRef?: React.Ref<HTMLDivElement>;
  variant?: MentionTimelineVariant;
};

const MentionTimelineItem = memo(function MentionTimelineItem({
  mention,
  allMentions,
  isActive,
  selectionEnabled,
  selected,
  disabled,
  onToggleSelected,
  onSelect,
  activeItemRef,
  variant = "rail",
}: MentionTimelineItemProps) {
  const isRejected = isRejectedMention(mention);
  const isReady = isMentionReviewReady(mention);
  const isOrganic = isOrganicMention(mention);
  const readyAccentColor = isOrganic ? "#9c27b0" : undefined;
  const logoProps = getBrandLogoProps(
    resolveBrandLogoFromMentions(mention, allMentions),
  );
  const isStrip = variant === "strip";

  return (
    <ListItemButton
      selected={isActive}
      disabled={disabled}
      onClick={() => onSelect(mention._id)}
      ref={isActive ? activeItemRef : undefined}
      sx={{
        py: isStrip ? 0.75 : 0.875,
        px: isStrip ? 1 : 1.25,
        alignItems: "flex-start",
        flexShrink: 0,
        width: isStrip ? (selectionEnabled ? 156 : 132) : undefined,
        borderLeft: isStrip ? 0 : 3,
        borderBottom: isStrip ? 3 : 0,
        borderColor: placementTypeColor(mention.placementType),
        opacity: isRejected ? 0.5 : 1,
        "&.Mui-selected": {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.14)
              : theme.palette.grey[800],
          "&:hover": {
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.18)
                : theme.palette.grey[900],
          },
        },
        "&:hover": {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.08)
              : theme.palette.grey[200],
        },
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="flex-start" width="100%">
        {selectionEnabled && (
          <Checkbox
            size="small"
            checked={selected}
            disabled={disabled}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onToggleSelected(mention._id)}
            inputProps={{ "aria-label": `Select ${mention.brandName}` }}
            sx={{ p: 0.25, mt: 0.125 }}
          />
        )}
        <Avatar
          src={logoProps.src}
          imgProps={{ onError: logoProps.onError }}
          sx={{ width: 24, height: 24, mt: 0.125, flexShrink: 0 }}
        />
        <Box minWidth={0} flex={1}>
          <Stack direction="row" alignItems="center" spacing={0.25} minWidth={0}>
            <Typography
              variant="caption"
              fontWeight={isActive ? 700 : 600}
              noWrap
              title={mention.brandName}
              sx={{
                flex: 1,
                minWidth: 0,
                color: isActive ? "common.white" : "text.primary",
                textDecoration: isRejected ? "line-through" : "none",
              }}
            >
              {mention.brandName}
            </Typography>
            {isReady && !isRejected && (
              <CheckCircleIcon
                color={isOrganic ? "inherit" : "success"}
                sx={{
                  fontSize: 12,
                  flexShrink: 0,
                  ...(readyAccentColor ? { color: readyAccentColor } : {}),
                }}
                aria-label="Ready"
              />
            )}
          </Stack>
          {!isStrip && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={placementTypeLabel(mention.placementType)}
              sx={{ display: "block", fontSize: "0.65rem", lineHeight: 1.35 }}
            >
              {placementTypeLabel(mention.placementType)}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              fontFamily: "monospace",
              fontSize: "0.65rem",
              lineHeight: 1.35,
            }}
          >
            {formatMentionTime(mention.startTime)} –{" "}
            {formatMentionTime(mention.endTime)}
          </Typography>
        </Box>
      </Stack>
    </ListItemButton>
  );
});

const MentionTimelineSidebar: React.FC<MentionTimelineSidebarProps> = memo(
  function MentionTimelineSidebar({
    mentions,
    currentMentionId,
    selectionEnabled,
    onSelectionEnabledChange,
    selectedIds,
    onToggleSelected,
    onToggleAll,
    onSelect,
    disabled,
    variant = "rail",
  }) {
    const activeItemRef = useRef<HTMLDivElement | null>(null);
    const timelineMentions = useMemo(
      () => sortMentionsByStartTime(mentions),
      [mentions],
    );
    const isStrip = variant === "strip";

    useEffect(() => {
      activeItemRef.current?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }, [currentMentionId]);

    return (
      <Box
        sx={{
          width: isStrip ? "100%" : 200,
          flexShrink: 0,
          borderRight: isStrip ? 0 : 1,
          borderBottom: isStrip ? 1 : 0,
          borderColor: "divider",
          overflowX: isStrip ? "auto" : undefined,
          overflowY: isStrip ? "hidden" : "auto",
          bgcolor: subtleMutedSurfaceBg,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ px: 1, pt: 1, pb: 0.5, width: "100%" }}
        >
          {selectionEnabled && (
            <Checkbox
              size="small"
              checked={
                timelineMentions.length > 0 &&
                timelineMentions.every((mention) => selectedIds.has(mention._id))
              }
              indeterminate={
                timelineMentions.some((mention) => selectedIds.has(mention._id)) &&
                !timelineMentions.every((mention) => selectedIds.has(mention._id))
              }
              disabled={disabled || timelineMentions.length === 0}
              onChange={onToggleAll}
              inputProps={{ "aria-label": "Select all mentions" }}
              sx={{ p: 0.25 }}
            />
          )}
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            In video order
          </Typography>
          <Button
            size="small"
            variant={selectionEnabled ? "contained" : "text"}
            disabled={disabled}
            onClick={() => onSelectionEnabledChange(!selectionEnabled)}
            sx={{ ml: "auto", minWidth: 0, px: 1, py: 0.25 }}
          >
            Select
          </Button>
        </Stack>
        <List
          dense
          disablePadding
          sx={
            isStrip
              ? {
                  display: "flex",
                  flexDirection: "row",
                  gap: 0.5,
                  px: 1,
                  pb: 1,
                }
              : undefined
          }
        >
          {timelineMentions.map((mention) => (
            <MentionTimelineItem
              key={mention._id}
              mention={mention}
              allMentions={timelineMentions}
              isActive={mention._id === currentMentionId}
              selectionEnabled={selectionEnabled}
              selected={selectedIds.has(mention._id)}
              disabled={disabled}
              onToggleSelected={onToggleSelected}
              onSelect={onSelect}
              variant={variant}
              activeItemRef={
                mention._id === currentMentionId ? activeItemRef : undefined
              }
            />
          ))}
        </List>
      </Box>
    );
  },
);

export function isOrganicMention(mention: BrandMention): boolean {
  const type = mention.classification || "paid";
  return type === "organic" || type === "neutral";
}

export function isMentionReviewReady(mention: BrandMention): boolean {
  const status = mention.feedback?.status;
  return status === "confirmed" || status === "approved";
}

export function areAllReviewMentionsReady(mentions: BrandMention[]): boolean {
  const reviewable = getActiveReviewMentions(mentions);
  return (
    reviewable.length > 0 &&
    reviewable.every((m) => isMentionReviewReady(m))
  );
}

type YoutubeTimestampBarProps = {
  watchUrl: string;
  startTimeSec: number;
  thumbnailUrl: string | null;
  onOpen?: () => void;
};

const YoutubeTimestampBar: React.FC<YoutubeTimestampBarProps> = memo(
  function YoutubeTimestampBar({
    watchUrl,
    startTimeSec,
    thumbnailUrl,
    onOpen,
  }) {
    const scrubHint = `${formatModShortcut("←")} / ${formatModShortcut("→")} scrub · ${formatModShortcut("←", { shift: true })} / ${formatModShortcut("→", { shift: true })} ±30s · ${formatModShortcut("k")} pause/play`;
    const openLabel =
      startTimeSec > 0 ? "Open at timestamp" : "Open on YouTube";

    return (
      <Box
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: subtleMutedSurfaceBg(theme),
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          minWidth: 0,
        })}
      >
        {thumbnailUrl && (
          <Box
            component="img"
            src={thumbnailUrl}
            alt=""
            sx={{
              width: 56,
              height: 32,
              objectFit: "cover",
              borderRadius: 0.5,
              flexShrink: 0,
              display: { xs: "none", lg: "block" },
            }}
          />
        )}
        <Tooltip title={scrubHint}>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.75}
            sx={{ minWidth: 0, flex: "1 1 auto" }}
          >
            <YouTubeIcon color="error" sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography variant="body2" fontWeight="medium" noWrap>
              {formatMentionTime(startTimeSec)}
            </Typography>
          </Stack>
        </Tooltip>
        {onOpen ? (
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={onOpen}
            startIcon={<YouTubeIcon />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{
              flexShrink: 0,
              maxWidth: "100%",
              "& .MuiButton-endIcon": { display: { xs: "none", sm: "inherit" } },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
              {openLabel}
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", lg: "none" } }}>
              Open
            </Box>
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            color="error"
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<YouTubeIcon />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{
              flexShrink: 0,
              maxWidth: "100%",
              "& .MuiButton-endIcon": { display: { xs: "none", sm: "inherit" } },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
              {openLabel}
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", lg: "none" } }}>
              Open
            </Box>
          </Button>
        )}
      </Box>
    );
  },
);

interface MentionTranscriptPanelProps {
  contentId: string;
  youtubeBar?: YoutubeTimestampBarProps | null;
  /** Seek the managed YouTube tab to a transcript segment start. */
  onSeekToTime?: (timeSec: number) => void;
  /** When set, italicizes the mention window and scrolls to it on load. */
  mentionWindow?: {
    startSec: number;
    endSec: number;
    scrollKey: string;
  } | null;
  helperText?: string;
}

const MentionTranscriptPanel: React.FC<MentionTranscriptPanelProps> = ({
  contentId,
  youtubeBar,
  onSeekToTime,
  mentionWindow = null,
  helperText = "Scroll for full episode transcript.",
}) => {
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledKeyRef = useRef<string | null>(null);
  const [transcriptSearchQuery, setTranscriptSearchQuery] = useState("");
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(0);

  const debouncedTranscriptSearch = useDebounce(transcriptSearchQuery, 200);
  const debouncedStart = useDebounce(mentionWindow?.startSec ?? 0, 400);
  const debouncedEnd = useDebounce(mentionWindow?.endSec ?? 0, 400);

  const { data: transcriptData, isLoading: transcriptLoading } = useQuery({
    queryKey: ["transcript-segments", contentId],
    queryFn: () => apiService.getContentTranscriptSegments(contentId),
    enabled: !!contentId,
    staleTime: 5 * 60 * 1000,
  });

  const transcriptSegments = transcriptData?.segments;
  const transcriptSegmentCount = transcriptSegments?.length ?? 0;

  const transcriptVirtualizer = useVirtualizer({
    count: transcriptSegmentCount,
    getScrollElement: () => transcriptScrollRef.current,
    estimateSize: () => 52,
    overscan: 10,
  });
  const transcriptVirtualizerRef = useRef(transcriptVirtualizer);
  transcriptVirtualizerRef.current = transcriptVirtualizer;

  const transcriptSearchMatches = useMemo(
    () =>
      findTranscriptSearchMatches(
        transcriptSegments ?? [],
        debouncedTranscriptSearch,
      ),
    [transcriptSegments, debouncedTranscriptSearch],
  );

  const activeTranscriptMatchKey =
    transcriptSearchMatches.length > 0
      ? transcriptSearchMatches[
          activeSearchMatchIndex % transcriptSearchMatches.length
        ]?.matchKey ?? null
      : null;

  const transcriptSearchOptions = useMemo(() => {
    const query = debouncedTranscriptSearch.trim();
    if (!query) return undefined;
    return {
      searchQuery: debouncedTranscriptSearch,
      activeMatchKey: activeTranscriptMatchKey,
    };
  }, [debouncedTranscriptSearch, activeTranscriptMatchKey]);

  useEffect(() => {
    setTranscriptSearchQuery("");
    setActiveSearchMatchIndex(0);
    lastScrolledKeyRef.current = null;
  }, [mentionWindow?.scrollKey, contentId]);

  useEffect(() => {
    setActiveSearchMatchIndex(0);
  }, [debouncedTranscriptSearch]);

  useEffect(() => {
    if (!mentionWindow || !transcriptSegments?.length) return;
    if (lastScrolledKeyRef.current === mentionWindow.scrollKey) return;

    lastScrolledKeyRef.current = mentionWindow.scrollKey;
    const segmentIndex = findTranscriptSegmentIndexForTime(
      transcriptSegments,
      debouncedStart,
    );
    requestAnimationFrame(() => {
      transcriptVirtualizerRef.current.scrollToIndex(segmentIndex, {
        align: "center",
      });
    });
  }, [
    mentionWindow?.scrollKey,
    mentionWindow,
    transcriptSegments,
    debouncedStart,
  ]);

  useEffect(() => {
    if (!activeTranscriptMatchKey || transcriptSearchMatches.length === 0) return;

    const match =
      transcriptSearchMatches[
        activeSearchMatchIndex % transcriptSearchMatches.length
      ];
    if (!match) return;

    requestAnimationFrame(() => {
      transcriptVirtualizerRef.current.scrollToIndex(match.segmentIndex, {
        align: "center",
      });
    });
  }, [
    activeTranscriptMatchKey,
    activeSearchMatchIndex,
    transcriptSearchMatches,
  ]);

  const goToNextTranscriptSearchMatch = useCallback(() => {
    if (transcriptSearchMatches.length === 0) return;
    setActiveSearchMatchIndex(
      (prev) => (prev + 1) % transcriptSearchMatches.length,
    );
  }, [transcriptSearchMatches.length]);

  const goToPreviousTranscriptSearchMatch = useCallback(() => {
    if (transcriptSearchMatches.length === 0) return;
    setActiveSearchMatchIndex(
      (prev) =>
        (prev - 1 + transcriptSearchMatches.length) %
        transcriptSearchMatches.length,
    );
  }, [transcriptSearchMatches.length]);

  const handleTranscriptSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (event.shiftKey) {
      goToPreviousTranscriptSearchMatch();
    } else {
      goToNextTranscriptSearchMatch();
    }
  };

  const { data: transcriptCoverage, isLoading: transcriptCoverageLoading } =
    useQuery({
      queryKey: ["transcript-coverage", contentId],
      queryFn: () => apiService.getContentTranscriptCoverage(contentId),
      enabled: !!contentId,
      staleTime: 5 * 60 * 1000,
    });

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: { xs: 200, md: 0 },
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {youtubeBar && <YoutubeTimestampBar {...youtubeBar} />}
      <Box
        sx={(theme) => ({
          flex: 1,
          minHeight: { xs: 200, md: 0 },
          p: 2,
          pt: youtubeBar ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: subtleMutedSurfaceBg(theme),
        })}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          Transcript
          {transcriptLoading && (
            <CircularProgress size={12} sx={{ ml: 1, verticalAlign: "middle" }} />
          )}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {helperText}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search transcript…"
            value={transcriptSearchQuery}
            onChange={(e) => setTranscriptSearchQuery(e.target.value)}
            onKeyDown={handleTranscriptSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Previous match (Shift+Enter)">
            <span>
              <IconButton
                size="small"
                onClick={goToPreviousTranscriptSearchMatch}
                disabled={
                  !debouncedTranscriptSearch.trim() ||
                  transcriptSearchMatches.length === 0
                }
                aria-label="Previous transcript search match"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next match (Enter)">
            <span>
              <IconButton
                size="small"
                onClick={goToNextTranscriptSearchMatch}
                disabled={
                  !debouncedTranscriptSearch.trim() ||
                  transcriptSearchMatches.length === 0
                }
                aria-label="Next transcript search match"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 56, textAlign: "right", flexShrink: 0 }}
          >
            {debouncedTranscriptSearch.trim()
              ? transcriptSearchMatches.length > 0
                ? `${
                    (activeSearchMatchIndex % transcriptSearchMatches.length) +
                    1
                  } / ${transcriptSearchMatches.length}`
                : "No matches"
              : ""}
          </Typography>
        </Stack>
        <Box ref={transcriptScrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {transcriptSegments && transcriptSegments.length > 0 ? (
            <Box
              sx={{
                height: transcriptVirtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
                fontSize: "0.8rem",
                lineHeight: 1.5,
                fontFamily: "monospace",
              }}
            >
              {transcriptVirtualizer.getVirtualItems().map((virtualRow) => {
                const seg = transcriptSegments[virtualRow.index]!;
                const index = virtualRow.index;
                return (
                  <Typography
                    key={virtualRow.key}
                    data-index={index}
                    ref={transcriptVirtualizer.measureElement}
                    component="div"
                    variant="body2"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      lineHeight: "inherit",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      pb: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        whiteSpace: "nowrap",
                        mr: 0.5,
                        verticalAlign: "middle",
                      }}
                    >
                      {formatTranscriptSegmentLabel(seg.start, seg.end)}
                      {onSeekToTime ? (
                        <Tooltip
                          title={`Go to ${formatMentionTime(seg.start)} on YouTube`}
                        >
                          <IconButton
                            size="small"
                            aria-label={`Go to ${formatMentionTime(seg.start)} on YouTube`}
                            onClick={() => onSeekToTime(seg.start)}
                            sx={{ p: 0.25 }}
                          >
                            <YouTubeIcon
                              color="error"
                              sx={{ fontSize: 16 }}
                            />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                    {renderSegmentTranscriptText(
                      seg,
                      mentionWindow ? debouncedStart : null,
                      mentionWindow ? debouncedEnd : null,
                      transcriptSearchOptions
                        ? {
                            ...transcriptSearchOptions,
                            segmentIndex: index,
                          }
                        : undefined,
                    )}
                  </Typography>
                );
              })}
            </Box>
          ) : transcriptLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading transcript…
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No transcript segments for this episode.
            </Typography>
          )}
        </Box>
        {(transcriptCoverageLoading || transcriptCoverage) && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: 1,
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Transcription coverage
            </Typography>
            {transcriptCoverageLoading ? (
              <Typography variant="body2" color="text.secondary">
                Calculating episode coverage…
              </Typography>
            ) : transcriptCoverage ? (
              <Typography variant="body2">
                Whole episode:{" "}
                <Box
                  component="span"
                  sx={{
                    color:
                      transcriptCoverage.missingPct > 0
                        ? "warning.main"
                        : "success.main",
                    fontWeight: 500,
                  }}
                >
                  {transcriptCoverage.missingPct.toFixed(1)}% missing
                </Box>
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface MentionReviewFormProps {
  mention: BrandMention;
  youtubeBar?: YoutubeTimestampBarProps | null;
  onSeekToTime?: (timeSec: number) => void;
  onSave: (
    mentionId: string,
    startTime: number,
    endTime: number,
    mentionType: string,
    commercialConfidence: number,
    brandName?: string,
    brandId?: string,
    visualCue?: VisualCue | null,
    placementType?: PlacementType,
    saveSource?: MentionSaveSource,
  ) => Promise<void>;
  onAliasesSaved?: (source: MentionSaveSource) => void;
  saveInProgress: boolean;
  onPreviewStartTimeChange?: (timeSec: number) => void;
}

const MentionReviewForm = forwardRef<MentionReviewFormHandle, MentionReviewFormProps>(
  function MentionReviewForm(
    {
      mention,
      youtubeBar,
      onSeekToTime,
      onSave,
      onAliasesSaved,
      saveInProgress,
      onPreviewStartTimeChange,
    },
    ref,
  ) {
  const queryClient = useQueryClient();
  const [startTime, setStartTime] = useState(mention.startTime);
  const [endTime, setEndTime] = useState(mention.endTime);
  const [mentionType, setMentionType] = useState(mention.classification || "");
  const [placementType, setPlacementType] = useState<PlacementType | "">(
    mention.placementType ?? "content",
  );
  const [commercialConfidence, setCommercialConfidence] = useState(
    mention.commercialConfidence || 0,
  );
  const [selectedBrand, setSelectedBrand] = useState(mention.brandName || "");
  const [selectedBrandId, setSelectedBrandId] = useState(mention.brandId || "");
  const [brandSearchQuery, setBrandSearchQuery] = useState(mention.brandName || "");
  const [visualCue, setVisualCue] = useState<VisualCue | "">(
    mention.visualCue ?? "",
  );
  const [aliases, setAliases] = useState<string[]>(mention.aliases || []);
  const [aliasesDirty, setAliasesDirty] = useState(false);
  const [aliasesSaving, setAliasesSaving] = useState(false);
  const [aliasesError, setAliasesError] = useState<string | null>(null);
  const [aliasesSaved, setAliasesSaved] = useState(false);
  const [createBrandDialogOpen, setCreateBrandDialogOpen] = useState(false);
  const [createBrandName, setCreateBrandName] = useState("");
  const [createBrandCategory, setCreateBrandCategory] = useState("unknown");
  const [createBrandSaving, setCreateBrandSaving] = useState(false);
  const [createBrandError, setCreateBrandError] = useState<string | null>(null);
  const [brandSearchFocused, setBrandSearchFocused] = useState(false);
  const brandSearchBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const debouncedSearchQuery = useDebounce(
    brandSearchQuery,
    BRAND_SEARCH_DEBOUNCE_MS,
  );
  const debouncedPreviewStart = useDebounce(startTime, 200);

  useEffect(() => {
    onPreviewStartTimeChange?.(debouncedPreviewStart);
  }, [debouncedPreviewStart, onPreviewStartTimeChange]);

  const { data: brandsData, isFetching: brandsFetching } = useQuery({
    queryKey: ["brands", debouncedSearchQuery || "all"],
    queryFn: () =>
      apiService.getBrands(1, 100, debouncedSearchQuery || undefined),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const filteredBrands = brandsData?.brands || [];
  const trimmedBrandQuery = brandSearchQuery.trim();
  const rankedBrands = useMemo(
    () => sortBrandsForSearch(filteredBrands, trimmedBrandQuery),
    [filteredBrands, trimmedBrandQuery],
  );

  const exactBrandNameMatch = useMemo(() => {
    const query = trimmedBrandQuery.toLowerCase();
    if (!query) return false;
    return filteredBrands.some((brand) => brand.name.toLowerCase() === query);
  }, [filteredBrands, trimmedBrandQuery]);

  const aliasExactMatches = useMemo(() => {
    const query = trimmedBrandQuery.toLowerCase();
    if (!query) return [];
    return filteredBrands.filter((brand) =>
      brand.aliases?.some((alias) => alias.toLowerCase() === query),
    );
  }, [filteredBrands, trimmedBrandQuery]);

  const createNameAliasOwners = useMemo(() => {
    const query = createBrandName.trim().toLowerCase();
    if (!query) return [];
    return filteredBrands.filter(
      (brand) =>
        brand.name.toLowerCase() !== query &&
        brand.aliases?.some((alias) => alias.toLowerCase() === query),
    );
  }, [filteredBrands, createBrandName]);

  const showBrandSuggestions =
    brandSearchFocused && trimmedBrandQuery.length >= 2;

  useEffect(() => {
    return () => {
      if (brandSearchBlurTimeoutRef.current) {
        clearTimeout(brandSearchBlurTimeoutRef.current);
      }
    };
  }, []);

  const handleBrandSearchFocus = () => {
    if (brandSearchBlurTimeoutRef.current) {
      clearTimeout(brandSearchBlurTimeoutRef.current);
      brandSearchBlurTimeoutRef.current = null;
    }
    setBrandSearchFocused(true);
  };

  const handleBrandSearchBlur = () => {
    brandSearchBlurTimeoutRef.current = setTimeout(() => {
      setBrandSearchFocused(false);
    }, 150);
  };

  const { data: brandCategoriesData } = useQuery({
    queryKey: ["brand-categories"],
    queryFn: () => apiService.getBrandCategories(),
    enabled: createBrandDialogOpen,
    staleTime: 5 * 60 * 1000,
  });

  const brandCategories = brandCategoriesData?.categories ?? [];

  const { data: brandData, isLoading: brandLoading, isError: brandLoadError } = useQuery({
    queryKey: ["brand", selectedBrandId],
    queryFn: () => apiService.getBrand(selectedBrandId!),
    enabled: !!selectedBrandId,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!selectedBrandId) {
      setAliases([]);
      setAliasesDirty(false);
      setAliasesError(null);
      setAliasesSaved(false);
      return;
    }
    if (brandData?.brand && !aliasesDirty) {
      setAliases(brandData.brand.aliases || []);
      setAliasesError(null);
      return;
    }
    if (brandLoadError && !aliasesDirty) {
      setAliases(mention.aliases || []);
      setAliasesError(null);
    }
  }, [
    selectedBrandId,
    brandData?.brand?._id,
    brandData?.brand?.aliases,
    brandLoadError,
    aliasesDirty,
    mention.aliases,
  ]);

  const saveAliases = useCallback(
    async (source: MentionSaveSource) => {
      if (!selectedBrandId) return false;
      setAliasesSaving(true);
      setAliasesError(null);
      try {
        const result = await apiService.updateBrandAliases(
          selectedBrandId,
          aliases,
        );
        setAliases(result.brand.aliases);
        setAliasesDirty(false);
        setAliasesSaved(source === "manual");
        await queryClient.invalidateQueries({
          queryKey: ["brand", selectedBrandId],
        });
        await queryClient.invalidateQueries({ queryKey: ["brands"] });
        await queryClient.invalidateQueries({
          queryKey: ["podcast-content-analysis-mentions"],
        });
        if (source === "manual") {
          onAliasesSaved?.(source);
        }
        return true;
      } catch {
        setAliasesError("Failed to save aliases.");
        throw new Error("Failed to save aliases");
      } finally {
        setAliasesSaving(false);
      }
    },
    [selectedBrandId, aliases, queryClient, onAliasesSaved],
  );

  const handleAliasesChange = (next: string[]) => {
    setAliases(next);
    setAliasesDirty(true);
    setAliasesSaved(false);
    setAliasesError(null);
  };

  const handleSaveAliases = () => {
    void saveAliases("manual");
  };

  const saveMention = useCallback(
    async (saveSource: MentionSaveSource) => {
      if (
        startTime >= 0 &&
        endTime > startTime &&
        mentionType &&
        commercialConfidence >= 0 &&
        selectedBrand
      ) {
        await onSave(
          mention._id,
          startTime,
          endTime,
          mentionType,
          commercialConfidence,
          selectedBrand,
          selectedBrandId,
          visualCue || null,
          (placementType || "content") as PlacementType,
          saveSource,
        );
      }
    },
    [
      startTime,
      endTime,
      mentionType,
      commercialConfidence,
      selectedBrand,
      selectedBrandId,
      visualCue,
      placementType,
      mention._id,
      onSave,
    ],
  );

  const isDirty = useCallback(() => {
    return (
      startTime !== mention.startTime ||
      endTime !== mention.endTime ||
      mentionType !== (mention.classification || "") ||
      (placementType || "content") !== (mention.placementType ?? "content") ||
      commercialConfidence !== (mention.commercialConfidence || 0) ||
      selectedBrand !== (mention.brandName || "") ||
      selectedBrandId !== (mention.brandId || "") ||
      (visualCue || null) !== (mention.visualCue ?? null)
    );
  }, [
    startTime,
    endTime,
    mentionType,
    placementType,
    commercialConfidence,
    selectedBrand,
    selectedBrandId,
    visualCue,
    mention,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      saveIfDirty: async () => {
        let mentionSaved = false;
        let aliasesSavedResult = false;

        if (aliasesDirty && selectedBrandId) {
          aliasesSavedResult = await saveAliases("auto");
        }

        if (isDirty()) {
          const canSave =
            startTime >= 0 &&
            endTime > startTime &&
            !!mentionType &&
            commercialConfidence >= 0 &&
            !!selectedBrand;
          if (canSave) {
            await saveMention("auto");
            mentionSaved = true;
          }
        }

        return { mention: mentionSaved, aliases: aliasesSavedResult };
      },
    }),
    [
      isDirty,
      saveMention,
      saveAliases,
      aliasesDirty,
      selectedBrandId,
      startTime,
      endTime,
      mentionType,
      commercialConfidence,
      selectedBrand,
    ],
  );

  const handleBrandSelect = (brandId: string, brandName: string) => {
    setSelectedBrandId(brandId);
    setSelectedBrand(brandName);
    setBrandSearchQuery(brandName);
    setAliasesDirty(false);
    setAliasesSaved(false);
    setAliasesError(null);
    setBrandSearchFocused(false);
  };

  const handleBrandSearchChange = (value: string) => {
    setBrandSearchQuery(value);
    const trimmed = value.trim();
    if (selectedBrandId && trimmed !== selectedBrand) {
      setSelectedBrandId("");
    }
    setSelectedBrand(trimmed);
  };

  const openCreateBrandDialog = (name?: string) => {
    setCreateBrandName(name ?? trimmedBrandQuery);
    setCreateBrandCategory("unknown");
    setCreateBrandError(null);
    setCreateBrandDialogOpen(true);
  };

  const handleCreateBrand = async () => {
    const name = createBrandName.trim();
    if (!name) {
      setCreateBrandError("Brand name is required.");
      return;
    }

    setCreateBrandSaving(true);
    setCreateBrandError(null);
    try {
      const result = await apiService.createBrand({
        name,
        category: createBrandCategory || "unknown",
        aliases: mention.brandName && mention.brandName.toLowerCase() !== name.toLowerCase()
          ? [mention.brandName]
          : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ["brands"] });
      handleBrandSelect(result.brand._id, result.brand.name);
      setCreateBrandDialogOpen(false);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { error?: string; brand?: { _id: string; name: string } };
        };
      };
      if (
        axiosError.response?.status === 409 &&
        axiosError.response.data?.brand
      ) {
        const existing = axiosError.response.data.brand;
        if (existing.name.trim().toLowerCase() === name.toLowerCase()) {
          await queryClient.invalidateQueries({ queryKey: ["brands"] });
          handleBrandSelect(existing._id, existing.name);
          setCreateBrandDialogOpen(false);
          return;
        }
        setCreateBrandError(
          `"${name}" is an alias of ${existing.name}. It was not created as its own brand.`,
        );
        return;
      }
      setCreateBrandError(
        axiosError.response?.data?.error || "Failed to create brand.",
      );
    } finally {
      setCreateBrandSaving(false);
    }
  };

  return (
    <Box
      component="form"
      id="mention-review-form"
      onSubmit={(e) => {
        e.preventDefault();
        void saveMention("manual");
      }}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: { xs: "1 1 0", md: "1 1 58%" },
          minWidth: 0,
          minHeight: 0,
          overflow: "auto",
          p: { xs: 2, md: 3 },
          borderRight: { md: 1 },
          borderColor: { md: "divider" },
        }}
      >
          <Box
            sx={(theme) => ({
              mb: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: subtleMutedSurfaceBg(theme),
            })}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Context
            </Typography>
            <Typography variant="body2">{mention.context || "—"}</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <DurationHmsInput
                label="Start Time"
                valueSeconds={startTime}
                onChange={setStartTime}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <DurationHmsInput
                label="End Time"
                valueSeconds={endTime}
                onChange={setEndTime}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Mention Type</InputLabel>
                <Select
                  value={mentionType}
                  label="Mention Type"
                  onChange={(e) => setMentionType(e.target.value)}
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="organic">Organic</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Placement</InputLabel>
                <Select
                  value={placementType}
                  label="Placement"
                  onChange={(e) =>
                    setPlacementType(e.target.value as PlacementType)
                  }
                >
                  {PLACEMENT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Commercial Confidence"
                type="number"
                value={commercialConfidence}
                onChange={(e) =>
                  setCommercialConfidence(Number(e.target.value))
                }
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText="0–100"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Visual Hint</InputLabel>
                <Select
                  value={visualCue}
                  label="Visual Hint"
                  onChange={(e) =>
                    setVisualCue(e.target.value as VisualCue | "")
                  }
                >
                  {VISUAL_CUE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.label} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box position="relative">
                <TextField
                  fullWidth
                  label="Search Brand"
                  value={brandSearchQuery}
                  onChange={(e) => handleBrandSearchChange(e.target.value)}
                  onFocus={handleBrandSearchFocus}
                  onBlur={handleBrandSearchBlur}
                  helperText={
                    selectedBrandId
                      ? `Linked to: ${selectedBrand}`
                      : selectedBrand
                        ? `Unlinked name: ${selectedBrand} (pick a brand or create new)`
                        : "Type to search brands or create a new one"
                  }
                  InputProps={{
                    endAdornment: brandsFetching ? (
                      <CircularProgress size={20} />
                    ) : undefined,
                  }}
                />
                {showBrandSuggestions && (
                  <Paper
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1300,
                      maxHeight: 240,
                      overflow: "auto",
                      mt: 1,
                    }}
                  >
                    <List dense disablePadding>
                      {rankedBrands.slice(0, 10).map((brand) => (
                        <ListItemButton
                          key={brand._id}
                          onClick={() =>
                            handleBrandSelect(brand._id, brand.name)
                          }
                          selected={selectedBrandId === brand._id}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="bold">
                                {brand.name}
                              </Typography>
                            }
                            secondary={
                              brand.aliases?.length
                                ? `Aliases: ${brand.aliases.slice(0, 3).join(", ")}`
                                : undefined
                            }
                          />
                        </ListItemButton>
                      ))}
                      {!exactBrandNameMatch && trimmedBrandQuery && (
                        <ListItemButton
                          onClick={() => {
                            setBrandSearchFocused(false);
                            openCreateBrandDialog();
                          }}
                          sx={{ borderTop: filteredBrands.length > 0 ? 1 : 0, borderColor: "divider" }}
                        >
                          <AddIcon fontSize="small" sx={{ mr: 1 }} />
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="medium">
                                Create brand &quot;{trimmedBrandQuery}&quot;
                              </Typography>
                            }
                            secondary={
                              aliasExactMatches.length > 0
                                ? `Not the same as alias match: ${aliasExactMatches.map((b) => b.name).join(", ")}`
                                : undefined
                            }
                          />
                        </ListItemButton>
                      )}
                    </List>
                  </Paper>
                )}
              </Box>
              {!selectedBrandId && mention.brandName && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => openCreateBrandDialog(mention.brandName)}
                  sx={{ mt: 0.5, alignSelf: "flex-start" }}
                >
                  Create brand from mention name
                </Button>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 1,
                  bgcolor: subtleMutedSurfaceBg(theme),
                })}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Brand aliases
                </Typography>
                {!selectedBrandId ? (
                  <Alert severity="info" sx={{ mb: 0 }}>
                    Select a linked brand above to manage aliases.
                  </Alert>
                ) : brandLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Alternative names used for brand detection.
                    </Typography>
                    <BrandStringListEditor
                      items={aliases}
                      onChange={handleAliasesChange}
                      addLabel="Add alias"
                      listLabel="Aliases"
                      emptyMessage="No aliases for this brand"
                    />
                    <Stack direction="row" alignItems="center" gap={1} mt={1.5}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveAliases}
                        disabled={!aliasesDirty || aliasesSaving}
                        startIcon={
                          aliasesSaving ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : undefined
                        }
                      >
                        {aliasesSaving ? "Saving…" : "Save aliases"}
                      </Button>
                      {aliasesSaved && !aliasesDirty && (
                        <Typography variant="caption" color="success.main">
                          Saved
                        </Typography>
                      )}
                      {aliasesError && (
                        <Typography variant="caption" color="error">
                          {aliasesError}
                        </Typography>
                      )}
                    </Stack>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
      </Box>

      <Box
        sx={{
          flex: { xs: "0 0 42%", md: "1 1 42%" },
          maxWidth: { md: "42%" },
          minWidth: { md: 280 },
          minHeight: { xs: 240, md: 0 },
          maxHeight: { xs: "46%", md: "none" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderTop: { xs: 1, md: 0 },
          borderColor: "divider",
        }}
      >
        <MentionTranscriptPanel
          contentId={mention.contentId}
          youtubeBar={youtubeBar}
          onSeekToTime={onSeekToTime}
          mentionWindow={{
            startSec: startTime,
            endSec: endTime,
            scrollKey: mention._id,
          }}
          helperText="Italic = current mention window. Scroll for full episode transcript."
        />
      </Box>

      <Dialog
        open={createBrandDialogOpen}
        onClose={() => !createBrandSaving && setCreateBrandDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create brand</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Brand name"
              value={createBrandName}
              onChange={(e) => setCreateBrandName(e.target.value)}
              disabled={createBrandSaving}
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={createBrandCategory}
                label="Category"
                onChange={(e) => setCreateBrandCategory(e.target.value)}
                disabled={createBrandSaving}
              >
                <MenuItem value="unknown">Unknown</MenuItem>
                {brandCategories
                  .filter((category) => category !== "unknown")
                  .map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {createNameAliasOwners.length > 0 && (
              <Alert severity="info">
                &quot;{createBrandName.trim()}&quot; is an alias of{" "}
                {createNameAliasOwners.map((brand) => brand.name).join(", ")}.
                Creating this brand removes that alias.
              </Alert>
            )}
            {createBrandError && (
              <Alert severity="error">{createBrandError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateBrandDialogOpen(false)}
            disabled={createBrandSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateBrand()}
            disabled={createBrandSaving || !createBrandName.trim()}
            startIcon={
              createBrandSaving ? <CircularProgress size={16} /> : <AddIcon />
            }
          >
            {createBrandSaving ? "Creating…" : "Create brand"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

interface EpisodeMentionReviewDialogProps {
  open: boolean;
  onClose: () => void;
  session: MentionReviewSession | null;
  saveInProgress: boolean;
  onSave: (
    mentionId: string,
    startTime: number,
    endTime: number,
    mentionType: string,
    commercialConfidence: number,
    brandName?: string,
    brandId?: string,
    visualCue?: VisualCue | null,
    placementType?: PlacementType,
    saveSource?: MentionSaveSource,
  ) => Promise<void>;
  onReadyChange: (mentionId: string, ready: boolean) => Promise<void>;
  onEpisodeReviewReadyChange?: (
    contentId: string,
    ready: boolean,
  ) => Promise<{ reviewReadyAt: string | null } | void>;
  onMentionsChange: (mentions: BrandMention[]) => void;
  onRejectMention?: (
    mentionId: string,
    reason?: string,
    context?: string,
  ) => Promise<void>;
  onRestoreMention?: (mentionId: string) => Promise<void>;
  onAddMissedMention?: (
    sourceMention: BrandMention | null,
    edits: {
      brands: Array<{ brandName: string; brandId?: string }>;
      startTime: number;
      endTime: number;
      mentionType: string;
      placementType: PlacementType;
      visualCue?: VisualCue | null;
    },
  ) => Promise<BrandMention[]>;
  onDeleteMention?: (mentionId: string) => Promise<void>;
  onCopyMentionId?: (mentionId: string) => void;
  onAliasesSaved?: (source: MentionSaveSource) => void;
  onAutoSaved?: () => void;
  onEditProgram?: (
    contentId: string,
    contentTitle: string,
    currentProgramId: string | undefined,
  ) => void;
  episodeNav?: {
    index: number;
    total: number;
    hasPrev: boolean;
    hasNext: boolean;
    onNavigate: (direction: -1 | 1) => void | Promise<void>;
  } | null;
}

const EpisodeMentionReviewDialog: React.FC<EpisodeMentionReviewDialogProps> = ({
  open,
  onClose,
  session,
  saveInProgress,
  onSave,
  onReadyChange,
  onEpisodeReviewReadyChange,
  onMentionsChange,
  onRejectMention,
  onRestoreMention,
  onAddMissedMention,
  onDeleteMention,
  onCopyMentionId,
  onAliasesSaved,
  onAutoSaved,
  onEditProgram,
  episodeNav,
}) => {
  const theme = useTheme();
  const isFullScreenDialog = useMediaQuery(theme.breakpoints.down("md"));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [readySaving, setReadySaving] = useState(false);
  const [episodeReadySaving, setEpisodeReadySaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [rejectInProgress, setRejectInProgress] = useState(false);
  const [addMissedOpen, setAddMissedOpen] = useState(false);
  const [addMissedSaving, setAddMissedSaving] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectContext, setRejectContext] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectScope, setRejectScope] = useState<"current" | "selected">("current");
  const [rejectSelectedCount, setRejectSelectedCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectedMentionIds, setSelectedMentionIds] = useState<string[]>([]);
  const [bulkStart, setBulkStart] = useState(0);
  const [bulkEnd, setBulkEnd] = useState(0);
  const [bulkPlacement, setBulkPlacement] = useState<PlacementType>("content");
  const [bulkVisualCue, setBulkVisualCue] = useState<VisualCue | "">("");
  const [bulkError, setBulkError] = useState<string | null>(null);
  const bulkSeededRef = useRef(false);
  const currentMentionIdRef = useRef<string | null>(null);
  const formRef = useRef<MentionReviewFormHandle>(null);
  const prevOpenRef = useRef(false);
  const lastYoutubeSeekKeyRef = useRef<string | null>(null);
  // Tracks which episode's YouTube tab we've already driven this dialog session.
  const youtubeSeekArmedContentIdRef = useRef<string | null>(null);

  const mentions = session?.mentions ?? [];
  const selectedMentionIdSet = useMemo(() => {
    const live = new Set(mentions.map((mention) => mention._id));
    return new Set(selectedMentionIds.filter((id) => live.has(id)));
  }, [mentions, selectedMentionIds]);
  const selectedRejectableMentions = useMemo(
    () =>
      mentions.filter(
        (mention) =>
          selectedMentionIdSet.has(mention._id) && !isRejectedMention(mention),
      ),
    [mentions, selectedMentionIdSet],
  );
  const reviewableMentions = useMemo(
    () => getActiveReviewMentions(mentions),
    [mentions],
  );
  const canNavigate = mentions.length > 1;
  const currentMention = mentions[currentIndex] ?? null;
  const timelineMentions = useMemo(
    () => sortMentionsByStartTime(mentions),
    [mentions],
  );
  const timelineIndex = useMemo(() => {
    if (!currentMention) return -1;
    return timelineMentions.findIndex((m) => m._id === currentMention._id);
  }, [currentMention, timelineMentions]);
  const isFirstInTimeline = timelineIndex <= 0;
  const isLastInTimeline =
    timelineIndex < 0 || timelineIndex >= timelineMentions.length - 1;
  const isCurrentRejected = currentMention
    ? isRejectedMention(currentMention)
    : false;

  useEffect(() => {
    setSelectionEnabled(false);
    setSelectedMentionIds([]);
    bulkSeededRef.current = false;
    setBulkError(null);
  }, [session?.contentId]);

  useEffect(() => {
    if (selectedMentionIdSet.size < 2) {
      bulkSeededRef.current = false;
      return;
    }
    if (bulkSeededRef.current || !currentMention) return;
    bulkSeededRef.current = true;
    setBulkStart(currentMention.startTime);
    setBulkEnd(currentMention.endTime);
    setBulkPlacement(currentMention.placementType ?? "content");
    setBulkVisualCue(currentMention.visualCue ?? "");
    setBulkError(null);
  }, [selectedMentionIdSet.size, currentMention]);
  const canMarkCurrentReady =
    !isCurrentRejected ||
    (currentMention != null && isOrganicMention(currentMention));
  const readyCount = reviewableMentions.filter(isMentionReviewReady).length;
  const [previewStartTime, setPreviewStartTime] = useState(0);

  useEffect(() => {
    setReviewDialogOpen(open);
    return () => setReviewDialogOpen(false);
  }, [open]);

  useEffect(() => {
    if (currentMention) {
      setPreviewStartTime(currentMention.startTime);
    }
  }, [currentMention?._id, currentMention?.startTime]);

  const youtubeWatchUrl = session?.contentUrl
    ? youtubeWatchUrlFromWatchUrl(
        session.contentUrl,
        currentMention ? previewStartTime : 0,
      )
    : null;

  const youtubeThumbnailUrl = useMemo(
    () =>
      session?.contentUrl
        ? youtubeThumbnailUrlFromWatchUrl(session.contentUrl)
        : null,
    [session?.contentUrl],
  );

  const seekYoutubeToTime = useCallback(
    (timeSec: number) => {
      if (!session?.contentUrl) return;
      seekYoutubeViaExtension(session.contentUrl, timeSec);
    },
    [session?.contentUrl],
  );

  const youtubeBar: YoutubeTimestampBarProps | null = useMemo(() => {
    if (!youtubeWatchUrl || !session?.contentUrl) return null;
    return {
      watchUrl: youtubeWatchUrl,
      startTimeSec: currentMention ? previewStartTime : 0,
      thumbnailUrl: youtubeThumbnailUrl,
      onOpen: () => {
        youtubeSeekArmedContentIdRef.current = session.contentId;
        seekYoutubeToTime(currentMention ? previewStartTime : 0);
      },
    };
  }, [
    youtubeWatchUrl,
    session?.contentUrl,
    session?.contentId,
    currentMention,
    previewStartTime,
    youtubeThumbnailUrl,
    seekYoutubeToTime,
  ]);

  useEffect(() => {
    if (!open) {
      lastYoutubeSeekKeyRef.current = null;
      youtubeSeekArmedContentIdRef.current = null;
      return;
    }
    if (!session?.contentUrl) return;

    // Open/reuse the saved YouTube tab as soon as the dialog opens.
    youtubeSeekArmedContentIdRef.current = session.contentId;

    const seekKey = !currentMention
      ? `${session.contentId}:__empty__`
      : `${session.contentId}:${currentMention._id}`;

    if (lastYoutubeSeekKeyRef.current === seekKey) return;
    lastYoutubeSeekKeyRef.current = seekKey;

    seekYoutubeToTime(currentMention ? currentMention.startTime : 0);
  }, [
    open,
    currentMention?._id,
    currentMention?.startTime,
    session?.contentId,
    session?.contentUrl,
    seekYoutubeToTime,
  ]);

  const sessionOpenKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      sessionOpenKeyRef.current = null;
      return;
    }

    if (!session) return;

    const sessionOpenKey = `${session.contentId}:${session.initialMentionId ?? `idx-${session.initialIndex}`}`;
    const justOpened = !prevOpenRef.current;
    const isNewSession = sessionOpenKeyRef.current !== sessionOpenKey;

    if (justOpened || isNewSession) {
      sessionOpenKeyRef.current = sessionOpenKey;

      let idx = 0;
      if (session.initialMentionId) {
        const byId = indexOfReviewMention(session.mentions, session.initialMentionId);
        idx = byId >= 0 ? byId : session.initialIndex;
      } else {
        idx = session.initialIndex;
      }

      idx = Math.max(0, Math.min(idx, Math.max(mentions.length - 1, 0)));
      setCurrentIndex(idx);
      currentMentionIdRef.current = mentions[idx]?._id ?? null;
    }

    prevOpenRef.current = true;
  }, [open, session, mentions]);

  useEffect(() => {
    if (!open) return;
    if (mentions.length === 0) {
      currentMentionIdRef.current = null;
      setCurrentIndex(0);
      return;
    }
    const mentionId = currentMentionIdRef.current;
    if (!mentionId) {
      currentMentionIdRef.current = mentions[0]?._id ?? null;
      setCurrentIndex(0);
      return;
    }
    const idx = mentions.findIndex((m) => m._id === mentionId);
    if (idx >= 0) {
      setCurrentIndex((prev) => (prev === idx ? prev : idx));
      return;
    }
    setCurrentIndex((prev) => {
      const next = Math.min(prev, mentions.length - 1);
      currentMentionIdRef.current = mentions[next]?._id ?? null;
      return next;
    });
  }, [mentions, open]);

  const goToIndex = useCallback(
    async (index: number) => {
      if (!canNavigate) return;
      const saved = (await formRef.current?.saveIfDirty()) ?? {
        mention: false,
        aliases: false,
      };
      if (saved.aliases && !saved.mention) {
        onAutoSaved?.();
      }
      const next = Math.max(0, Math.min(mentions.length - 1, index));
      currentMentionIdRef.current = mentions[next]?._id ?? null;
      setCurrentIndex(next);
    },
    [canNavigate, mentions, onAutoSaved],
  );

  const goToMentionId = useCallback(
    (mentionId: string) => {
      const idx = mentions.findIndex((m) => m._id === mentionId);
      if (idx >= 0) goToIndex(idx);
    },
    [mentions, goToIndex],
  );

  const goPrev = useCallback(() => {
    if (!currentMention || !canNavigate || isFirstInTimeline) return;
    const prevMention = timelineMentions[timelineIndex - 1];
    if (prevMention) goToMentionId(prevMention._id);
  }, [
    currentMention,
    canNavigate,
    isFirstInTimeline,
    timelineMentions,
    timelineIndex,
    goToMentionId,
  ]);

  const goNext = useCallback(() => {
    if (!currentMention || !canNavigate || isLastInTimeline) return;
    const nextMention = timelineMentions[timelineIndex + 1];
    if (nextMention) goToMentionId(nextMention._id);
  }, [
    currentMention,
    canNavigate,
    isLastInTimeline,
    timelineMentions,
    timelineIndex,
    goToMentionId,
  ]);

  const handleSelectTimelineMention = useCallback(
    (mentionId: string) => {
      goToMentionId(mentionId);
    },
    [goToMentionId],
  );

  const handleClose = useCallback(async () => {
    setAutoSaving(true);
    try {
      const saved = (await formRef.current?.saveIfDirty()) ?? {
        mention: false,
        aliases: false,
      };
      if (saved.aliases && !saved.mention) {
        onAutoSaved?.();
      }
      onClose();
    } catch {
      // Stay open on save failure.
    } finally {
      setAutoSaving(false);
    }
  }, [onAutoSaved, onClose]);

  const navigationBusy =
    saveInProgress ||
    autoSaving ||
    rejectInProgress ||
    addMissedSaving ||
    deleteInProgress;

  const setMentionSelectionEnabled = useCallback((enabled: boolean) => {
    setSelectionEnabled(enabled);
    if (!enabled) {
      setSelectedMentionIds([]);
      bulkSeededRef.current = false;
      setBulkError(null);
    }
  }, []);

  const toggleMentionSelected = useCallback((mentionId: string) => {
    setSelectedMentionIds((prev) =>
      prev.includes(mentionId)
        ? prev.filter((id) => id !== mentionId)
        : [...prev, mentionId],
    );
    setBulkError(null);
  }, []);

  const toggleAllMentionsSelected = useCallback(() => {
    setSelectedMentionIds((prev) => {
      const live = new Set(mentions.map((mention) => mention._id));
      const selected = prev.filter((id) => live.has(id));
      const allSelected =
        mentions.length > 0 && selected.length === mentions.length;
      return allSelected ? [] : mentions.map((mention) => mention._id);
    });
    setBulkError(null);
  }, [mentions]);

  const applyBulkEdits = useCallback(async () => {
    if (bulkEnd <= bulkStart) {
      setBulkError("End time has to be after start time.");
      return;
    }
    const targets = mentions.filter((mention) =>
      selectedMentionIdSet.has(mention._id),
    );
    if (targets.length < 2) return;
    setBulkError(null);
    try {
      for (const mention of targets) {
        await onSave(
          mention._id,
          bulkStart,
          bulkEnd,
          mention.classification || "paid",
          mention.commercialConfidence || 0,
          mention.brandName,
          mention.brandId,
          bulkVisualCue || null,
          bulkPlacement,
          "manual",
        );
      }
    } catch {
      setBulkError("Could not update every selected mention.");
    }
  }, [
    bulkEnd,
    bulkStart,
    bulkPlacement,
    bulkVisualCue,
    mentions,
    onSave,
    selectedMentionIdSet,
  ]);

  const goToEpisode = useCallback(
    async (direction: -1 | 1) => {
      if (!episodeNav) return;
      if (direction < 0 && !episodeNav.hasPrev) return;
      if (direction > 0 && !episodeNav.hasNext) return;
      if (navigationBusy || readySaving) return;

      setAutoSaving(true);
      try {
        const saved = (await formRef.current?.saveIfDirty()) ?? {
          mention: false,
          aliases: false,
        };
        if (saved.aliases && !saved.mention) {
          onAutoSaved?.();
        }
        await episodeNav.onNavigate(direction);
      } catch {
        // Stay on current episode on save failure.
      } finally {
        setAutoSaving(false);
      }
    },
    [episodeNav, navigationBusy, readySaving, onAutoSaved],
  );

  const handleToggleReady = useCallback(async () => {
    if (!currentMention) return;
    setReadySaving(true);
    try {
      await formRef.current?.saveIfDirty();
      const nextReady = !isMentionReviewReady(currentMention);
      await onReadyChange(currentMention._id, nextReady);
    } finally {
      setReadySaving(false);
    }
  }, [currentMention, onReadyChange]);

  const handleToggleEpisodeReady = useCallback(async () => {
    if (!session || !onEpisodeReviewReadyChange) return;
    setEpisodeReadySaving(true);
    try {
      const nextReady = !session.reviewReadyAt;
      await onEpisodeReviewReadyChange(session.contentId, nextReady);
    } finally {
      setEpisodeReadySaving(false);
    }
  }, [session, onEpisodeReviewReadyChange]);

  const handleRestoreCurrentMention = useCallback(async () => {
    if (!currentMention || !onRestoreMention || restoreInProgress) return;
    setRestoreInProgress(true);
    try {
      await onRestoreMention(currentMention._id);
    } finally {
      setRestoreInProgress(false);
    }
  }, [currentMention, onRestoreMention, restoreInProgress]);

  const rejectCurrentMention = useCallback(
    async (reason?: string, context?: string) => {
      if (
        !currentMention ||
        !onRejectMention ||
        rejectInProgress ||
        isRejectedMention(currentMention)
      ) {
        return;
      }

      setRejectInProgress(true);
      try {
        await onRejectMention(currentMention._id, reason, context);
        setRejectDialogOpen(false);
        setRejectReason("");
        setRejectContext("");
        if (currentIndex >= mentions.length - 1 && currentIndex > 0) {
          setCurrentIndex((i) => Math.max(0, i - 1));
        }
      } finally {
        setRejectInProgress(false);
      }
    },
    [
      currentMention,
      onRejectMention,
      rejectInProgress,
      currentIndex,
      mentions.length,
    ],
  );

  const rejectSelectedMentions = useCallback(
    async (reason?: string, context?: string) => {
      if (!onRejectMention || rejectInProgress) return;
      const targets = mentions.filter(
        (mention) =>
          selectedMentionIdSet.has(mention._id) && !isRejectedMention(mention),
      );
      if (targets.length === 0) return;

      setRejectInProgress(true);
      setBulkError(null);
      try {
        for (const mention of targets) {
          await onRejectMention(mention._id, reason, context);
        }
        setRejectDialogOpen(false);
        setRejectReason("");
        setRejectContext("");
        setSelectedMentionIds([]);
      } catch {
        setBulkError("Could not mark every selected mention as not a mention.");
      } finally {
        setRejectInProgress(false);
      }
    },
    [mentions, onRejectMention, rejectInProgress, selectedMentionIdSet],
  );

  const openRejectDialog = useCallback((scope: "current" | "selected") => {
    setRejectScope(scope);
    if (scope === "selected") {
      setRejectSelectedCount(selectedRejectableMentions.length);
    }
    setRejectReason(DEFAULT_REJECT_REASON);
    setRejectContext("");
    setRejectDialogOpen(true);
  }, [selectedRejectableMentions.length]);

  const subDialogOpen = rejectDialogOpen || addMissedOpen || deleteDialogOpen;

  useEffect(() => {
    if (!open || !session) return;

    const armYoutubeSeek = () => {
      youtubeSeekArmedContentIdRef.current = session.contentId;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      if (
        currentMention &&
        !subDialogOpen &&
        canNavigate &&
        !mod &&
        !isTypingTarget(e.target)
      ) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          goPrev();
          return;
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          goNext();
          return;
        }
      }

      if (!mod) return;
      if (subDialogOpen) return;

      const key = e.key.toLowerCase();

      // ⌘⌫ — reject (not a mention). Delete stays on ⌘⇧⌫.
      if (
        currentMention &&
        !e.shiftKey &&
        key === "backspace" &&
        onRejectMention &&
        !isCurrentRejected
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (!navigationBusy && !rejectInProgress) {
          void rejectCurrentMention(DEFAULT_REJECT_REASON);
        }
        return;
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (e.altKey && episodeNav) {
          void goToEpisode(e.key === "ArrowLeft" ? -1 : 1);
          return;
        }
        armYoutubeSeek();
        const direction = (e.key === "ArrowLeft" ? -1 : 1) as -1 | 1;
        const stepSec = e.shiftKey ? 30 : 5;
        void nudgeManagedYoutubeTime(direction, stepSec).then((currentTime) => {
          if (currentTime != null) {
            setPreviewStartTime(currentTime);
            return;
          }
          setPreviewStartTime((prev) => {
            const newTime = Math.max(0, prev + direction * stepSec);
            seekYoutubeToTime(newTime);
            return newTime;
          });
        });
        return;
      }

      if (key === "k") {
        e.preventDefault();
        armYoutubeSeek();
        toggleManagedYoutubePlayback();
        return;
      }

      if (key === "m" && !e.shiftKey) {
        e.preventDefault();
        armYoutubeSeek();
        toggleManagedYoutubeMute();
        return;
      }

      if (currentMention && key === "s") {
        e.preventDefault();
        if (!navigationBusy) {
          const form = document.getElementById("mention-review-form");
          if (form instanceof HTMLFormElement) form.requestSubmit();
        }
        return;
      }

      if (isTypingTarget(e.target)) return;

      if (key === "enter" && !e.shiftKey) {
        e.preventDefault();
        if (currentMention) {
          if (!navigationBusy && !readySaving && canMarkCurrentReady) {
            void handleToggleReady();
          }
        } else if (
          session &&
          onEpisodeReviewReadyChange &&
          !navigationBusy &&
          !episodeReadySaving
        ) {
          void handleToggleEpisodeReady();
        }
        return;
      }

      if (
        key === "enter" &&
        e.shiftKey &&
        onEpisodeReviewReadyChange &&
        session
      ) {
        e.preventDefault();
        if (!navigationBusy && !episodeReadySaving) {
          void handleToggleEpisodeReady();
        }
        return;
      }

      if (key === "w") {
        e.preventDefault();
        if (!navigationBusy) void handleClose();
        return;
      }

      if (e.shiftKey && key === "m" && onAddMissedMention) {
        e.preventDefault();
        if (!navigationBusy) setAddMissedOpen(true);
        return;
      }

      if (e.shiftKey && key === "u" && onRestoreMention && isCurrentRejected) {
        e.preventDefault();
        if (!restoreInProgress && !navigationBusy) {
          void handleRestoreCurrentMention();
        }
        return;
      }

      if (
        currentMention &&
        e.shiftKey &&
        key === "backspace" &&
        onDeleteMention
      ) {
        e.preventDefault();
        if (!navigationBusy) setDeleteDialogOpen(true);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    open,
    session,
    currentMention,
    subDialogOpen,
    canNavigate,
    goPrev,
    goNext,
    navigationBusy,
    readySaving,
    canMarkCurrentReady,
    episodeReadySaving,
    onEpisodeReviewReadyChange,
    restoreInProgress,
    rejectInProgress,
    isCurrentRejected,
    handleToggleReady,
    handleToggleEpisodeReady,
    handleRestoreCurrentMention,
    rejectCurrentMention,
    handleClose,
    goToEpisode,
    seekYoutubeToTime,
    onAddMissedMention,
    onRejectMention,
    onRestoreMention,
    onDeleteMention,
    episodeNav,
    currentIndex,
    mentions.length,
  ]);

  const handleConfirmReject = useCallback(async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    const context = rejectContext.trim() || undefined;
    if (rejectScope === "selected") {
      await rejectSelectedMentions(reason, context);
      return;
    }
    await rejectCurrentMention(reason, context);
  }, [
    rejectCurrentMention,
    rejectReason,
    rejectContext,
    rejectScope,
    rejectSelectedMentions,
  ]);

  const handleDeleteCurrentMention = useCallback(async () => {
    if (!currentMention || !onDeleteMention || deleteInProgress) return;

    const mentionIdToDelete = currentMention._id;
    const nextMentionId =
      !isLastInTimeline && timelineMentions[timelineIndex + 1]
        ? timelineMentions[timelineIndex + 1]._id
        : timelineIndex > 0
          ? timelineMentions[timelineIndex - 1]._id
          : null;

    setDeleteInProgress(true);
    try {
      if (nextMentionId) {
        currentMentionIdRef.current = nextMentionId;
      }
      await onDeleteMention(mentionIdToDelete);
      setDeleteDialogOpen(false);
      if (nextMentionId) {
        goToMentionId(nextMentionId);
      }
    } finally {
      setDeleteInProgress(false);
    }
  }, [
    currentMention,
    onDeleteMention,
    deleteInProgress,
    isLastInTimeline,
    timelineMentions,
    timelineIndex,
    goToMentionId,
  ]);

  useEffect(() => {
    const confirmDelete = deleteDialogOpen && !deleteInProgress;
    const confirmReject = rejectDialogOpen && !rejectInProgress;
    if (!confirmDelete && !confirmReject) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.shiftKey || e.key !== "Enter") return;

      e.preventDefault();
      e.stopPropagation();
      if (confirmDelete) {
        void handleDeleteCurrentMention();
      } else if (confirmReject) {
        void handleConfirmReject();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    deleteDialogOpen,
    rejectDialogOpen,
    deleteInProgress,
    rejectInProgress,
    handleDeleteCurrentMention,
    handleConfirmReject,
  ]);

  const handleEditProgramClick = useCallback(() => {
    if (!session || !onEditProgram) return;
    onEditProgram(
      session.contentId,
      session.contentTitle,
      session.programId,
    );
  }, [session, onEditProgram]);

  const programChip = onEditProgram ? (
    <Chip
      label={session?.programName || "Set program"}
      size="small"
      color={session?.programId ? "secondary" : "default"}
      variant="outlined"
      icon={<EditIcon sx={{ fontSize: "14px !important" }} />}
      onClick={handleEditProgramClick}
      disabled={navigationBusy || readySaving || episodeReadySaving}
      sx={{
        fontSize: "0.75rem",
        height: 24,
        fontWeight: 600,
        maxWidth: 180,
        cursor: "pointer",
        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
        "& .MuiChip-icon": { ml: 0.75, mr: -0.25 },
      }}
      title={
        session?.programName
          ? `Program: ${session.programName} (click to edit)`
          : "Click to set program"
      }
    />
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={() => void handleClose()}
      maxWidth={false}
      fullWidth
      fullScreen={isFullScreenDialog}
      PaperProps={{
        sx: {
          width: isFullScreenDialog ? "100%" : "96vw",
          maxWidth: isFullScreenDialog ? "100%" : "96vw",
          height: isFullScreenDialog ? "100%" : "92vh",
          maxHeight: isFullScreenDialog ? "100%" : "92vh",
          m: isFullScreenDialog ? 0 : 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: isFullScreenDialog ? 0 : undefined,
        },
      }}
    >
      {session && currentMention ? (
        <>
          <DialogTitle sx={{ pb: 1, px: { xs: 1.5, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
            <Stack spacing={1} sx={{ minWidth: 0 }}>
              <Stack
                alignItems={{ xs: "stretch", lg: "center" }}
                justifyContent="space-between"
                gap={1}
                sx={{
                  minWidth: 0,
                  flexDirection: { xs: "column", lg: "row" },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ minWidth: 0, flex: { lg: "1 1 auto" } }}
                >
                  <Button
                    size="small"
                    onClick={() => void handleClose()}
                    disabled={navigationBusy || readySaving}
                    sx={{ flexShrink: 0 }}
                  >
                    Close
                    <ButtonShortcutLabel shortcut={formatModShortcut("w")} />
                  </Button>
                  {session.contentTitle && (
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      noWrap
                      sx={{ flex: 1, minWidth: 0 }}
                      title={session.contentTitle}
                    >
                      {session.contentTitle}
                    </Typography>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    justifyContent: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  {onAddMissedMention && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={navigationBusy || readySaving}
                      onClick={() => setAddMissedOpen(true)}
                      startIcon={<AddIcon />}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        AI missed mention
                      </Box>
                      <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                        Missed
                      </Box>
                      <ButtonShortcutLabel shortcut={formatModShortcut("m", { shift: true })} />
                    </Button>
                  )}
                  {onRejectMention && !isCurrentRejected && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={navigationBusy || readySaving}
                      onClick={() => openRejectDialog("current")}
                      startIcon={
                        rejectInProgress ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ThumbDownIcon />
                        )
                      }
                    >
                      {rejectInProgress ? "Rejecting…" : "Not a mention"}
                      {!rejectInProgress && (
                        <ButtonShortcutLabel shortcut={formatModBackspaceShortcut()} />
                      )}
                    </Button>
                  )}
                  {onRestoreMention && isCurrentRejected && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={
                        navigationBusy ||
                        readySaving ||
                        restoreInProgress
                      }
                      onClick={() => void handleRestoreCurrentMention()}
                    >
                      {restoreInProgress ? "Restoring…" : "Restore mention"}
                      {!restoreInProgress && (
                        <ButtonShortcutLabel shortcut={formatModShortcut("u", { shift: true })} />
                      )}
                    </Button>
                  )}
                  {onDeleteMention && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={navigationBusy || readySaving}
                      onClick={() => setDeleteDialogOpen(true)}
                      startIcon={<DeleteIcon />}
                    >
                      Delete
                      <ButtonShortcutLabel shortcut={formatModShiftBackspaceShortcut()} />
                    </Button>
                  )}
                  {onCopyMentionId && currentMention && (
                    <Tooltip title="Copy mention ID">
                      <IconButton
                        size="small"
                        disabled={navigationBusy || readySaving}
                        onClick={() => onCopyMentionId(currentMention._id)}
                        aria-label="Copy mention ID"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    variant={isMentionReviewReady(currentMention) ? "outlined" : "contained"}
                    color="success"
                    disabled={
                      navigationBusy ||
                      readySaving ||
                      !canMarkCurrentReady
                    }
                    onClick={handleToggleReady}
                    startIcon={
                      readySaving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : isMentionReviewReady(currentMention) ? (
                        <CheckCircleIcon />
                      ) : undefined
                    }
                  >
                    {isMentionReviewReady(currentMention) ? "Mark pending" : "Mark ready"}
                    <ButtonShortcutLabel shortcut={formatModShortcut("↩")} />
                  </Button>
                  {onEpisodeReviewReadyChange && (
                    <Button
                      size="small"
                      variant={session.reviewReadyAt ? "outlined" : "contained"}
                      color={session.reviewReadyAt ? "inherit" : "success"}
                      disabled={navigationBusy || episodeReadySaving || readySaving}
                      onClick={() => void handleToggleEpisodeReady()}
                      startIcon={
                        episodeReadySaving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : session.reviewReadyAt ? (
                          <CheckCircleIcon />
                        ) : undefined
                      }
                    >
                      {session.reviewReadyAt
                        ? "Mark episode pending"
                        : "Mark episode ready"}
                      <ButtonShortcutLabel
                        shortcut={formatModShortcut("↩", { shift: true })}
                      />
                    </Button>
                  )}
                  <Button
                    size="small"
                    type="submit"
                    form="mention-review-form"
                    variant="contained"
                    disabled={navigationBusy}
                    startIcon={
                      saveInProgress || autoSaving ? (
                        <CircularProgress size={16} />
                      ) : (
                        <EditIcon />
                      )
                    }
                  >
                    {saveInProgress || autoSaving ? "Saving..." : "Save"}
                    {!saveInProgress && !autoSaving && (
                      <ButtonShortcutLabel shortcut={formatModShortcut("s")} />
                    )}
                  </Button>
                  {canNavigate && !isLastInTimeline ? (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<ChevronRightIcon />}
                      onClick={goNext}
                      disabled={navigationBusy || readySaving}
                    >
                      Next
                    </Button>
                  ) : episodeNav?.hasNext ? (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<ChevronRightIcon />}
                      onClick={() => void goToEpisode(1)}
                      disabled={navigationBusy || readySaving}
                    >
                      Next episode
                      <ButtonShortcutLabel
                        shortcut={IS_APPLE_PLATFORM ? "⌘⌥→" : "Ctrl+Alt+→"}
                      />
                    </Button>
                  ) : null}
                </Stack>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ minWidth: 0 }}
              >
                {episodeNav && episodeNav.total > 1 && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip
                      title={
                        IS_APPLE_PLATFORM
                          ? "Previous episode (⌘⌥←)"
                          : "Previous episode (Ctrl+Alt+←)"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void goToEpisode(-1)}
                          disabled={!episodeNav.hasPrev || navigationBusy || readySaving}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 72, textAlign: "center" }}
                    >
                      Ep {episodeNav.index + 1} / {episodeNav.total}
                    </Typography>
                    <Tooltip
                      title={
                        IS_APPLE_PLATFORM
                          ? "Next episode (⌘⌥→)"
                          : "Next episode (Ctrl+Alt+→)"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void goToEpisode(1)}
                          disabled={!episodeNav.hasNext || navigationBusy || readySaving}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                )}
                {programChip}
                {canNavigate && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip title="Previous mention (←)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={goPrev}
                          disabled={isFirstInTimeline || navigationBusy}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 72, textAlign: "center" }}
                    >
                      {timelineIndex >= 0 ? timelineIndex + 1 : currentIndex + 1} /{" "}
                      {mentions.length}
                    </Typography>
                    <Tooltip title="Next mention (→)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={goNext}
                          disabled={isLastInTimeline || navigationBusy}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                )}
                {canNavigate && (
                  <Typography variant="caption" color="text.secondary">
                    {readyCount} / {reviewableMentions.length} ready
                  </Typography>
                )}
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
            }}
          >
            <MentionTimelineSidebar
              mentions={mentions}
              currentMentionId={currentMention._id}
              selectionEnabled={selectionEnabled}
              onSelectionEnabledChange={setMentionSelectionEnabled}
              selectedIds={selectedMentionIdSet}
              onToggleSelected={toggleMentionSelected}
              onToggleAll={toggleAllMentionsSelected}
              onSelect={handleSelectTimelineMention}
              disabled={navigationBusy}
              variant={isFullScreenDialog ? "strip" : "rail"}
            />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {isCurrentRejected && (
                <Alert severity="warning" sx={{ mx: 3, mt: 2, mb: 0 }}>
                  Marked as not a mention
                  {currentMention.feedback?.reason
                    ? `: "${currentMention.feedback.reason}"`
                    : ""}
                  .
                  {currentMention.feedback?.context ? (
                    <>
                      {" "}
                      Context: {currentMention.feedback.context}.
                    </>
                  ) : null}
                  {isOrganicMention(currentMention)
                    ? " Save as organic or use Mark ready to confirm it as an organic mention."
                    : " You can still edit it, or restore it as a mention."}
                </Alert>
              )}
              {selectionEnabled && selectedMentionIdSet.size >= 1 && (
                <Box
                  sx={{
                    px: { xs: 2, md: 3 },
                    pt: 2,
                    pb: 0,
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: subtleMutedSurfaceBg(theme),
                    })}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: selectedMentionIdSet.size >= 2 ? 1.5 : 0 }}
                    >
                      <Typography variant="subtitle2">
                        {selectedMentionIdSet.size === 1
                          ? "1 selected mention"
                          : `Edit ${selectedMentionIdSet.size} selected mentions`}
                      </Typography>
                      {onRejectMention && selectedRejectableMentions.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={navigationBusy}
                          onClick={() => openRejectDialog("selected")}
                          startIcon={
                            rejectInProgress ? (
                              <CircularProgress size={16} />
                            ) : (
                              <ThumbDownIcon />
                            )
                          }
                        >
                          {rejectInProgress
                            ? "Rejecting…"
                            : selectedRejectableMentions.length === 1
                              ? "Not a mention"
                              : `Not a mention (${selectedRejectableMentions.length})`}
                        </Button>
                      )}
                    </Stack>
                    {selectedMentionIdSet.size >= 2 && (
                    <>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <DurationHmsInput
                          label="Start Time"
                          valueSeconds={bulkStart}
                          onChange={setBulkStart}
                          disabled={navigationBusy}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <DurationHmsInput
                          label="End Time"
                          valueSeconds={bulkEnd}
                          onChange={setBulkEnd}
                          disabled={navigationBusy}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <FormControl fullWidth disabled={navigationBusy}>
                          <InputLabel>Placement</InputLabel>
                          <Select
                            value={bulkPlacement}
                            label="Placement"
                            onChange={(event) =>
                              setBulkPlacement(event.target.value as PlacementType)
                            }
                          >
                            {PLACEMENT_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <FormControl fullWidth disabled={navigationBusy}>
                          <InputLabel>Visual Hint</InputLabel>
                          <Select
                            value={bulkVisualCue}
                            label="Visual Hint"
                            onChange={(event) =>
                              setBulkVisualCue(event.target.value as VisualCue | "")
                            }
                          >
                            {VISUAL_CUE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.label} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Button
                      variant="contained"
                      sx={{ mt: 1.5 }}
                      disabled={navigationBusy}
                      onClick={() => void applyBulkEdits()}
                      startIcon={
                        saveInProgress ? <CircularProgress size={16} /> : <EditIcon />
                      }
                    >
                      {saveInProgress
                        ? "Saving..."
                        : `Apply to ${selectedMentionIdSet.size} mentions`}
                    </Button>
                    </>
                    )}
                    {bulkError && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        {bulkError}
                      </Alert>
                    )}
                  </Box>
                </Box>
              )}
              <MentionReviewForm
                ref={formRef}
                key={currentMention._id}
                mention={currentMention}
                youtubeBar={youtubeBar}
                onSeekToTime={youtubeWatchUrl ? seekYoutubeToTime : undefined}
                onPreviewStartTimeChange={setPreviewStartTime}
                onSave={onSave}
                onAliasesSaved={onAliasesSaved}
                saveInProgress={saveInProgress || autoSaving}
              />
            </Box>
          </DialogContent>
        </>
      ) : session ? (
        <>
          <DialogTitle sx={{ pb: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              flexWrap="wrap"
              useFlexGap
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ minWidth: 0 }}
              >
                <Button
                  size="small"
                  onClick={() => void handleClose()}
                  disabled={navigationBusy}
                >
                  Close
                  <ButtonShortcutLabel shortcut={formatModShortcut("w")} />
                </Button>
                {episodeNav && episodeNav.total > 1 && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip
                      title={
                        IS_APPLE_PLATFORM
                          ? "Previous episode (⌘⌥←)"
                          : "Previous episode (Ctrl+Alt+←)"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void goToEpisode(-1)}
                          disabled={!episodeNav.hasPrev || navigationBusy}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 72, textAlign: "center" }}
                    >
                      Ep {episodeNav.index + 1} / {episodeNav.total}
                    </Typography>
                    <Tooltip
                      title={
                        IS_APPLE_PLATFORM
                          ? "Next episode (⌘⌥→)"
                          : "Next episode (Ctrl+Alt+→)"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void goToEpisode(1)}
                          disabled={!episodeNav.hasNext || navigationBusy}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                )}
                <Typography variant="subtitle1" noWrap sx={{ minWidth: 0 }}>
                  {session.contentTitle}
                </Typography>
                {programChip}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                useFlexGap
              >
                {onAddMissedMention && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={navigationBusy || episodeReadySaving}
                    onClick={() => setAddMissedOpen(true)}
                    startIcon={<AddIcon />}
                  >
                    Add mention
                    <ButtonShortcutLabel shortcut={formatModShortcut("m", { shift: true })} />
                  </Button>
                )}
                {onEpisodeReviewReadyChange && (
                  <Button
                    size="small"
                    variant={session.reviewReadyAt ? "outlined" : "contained"}
                    color="success"
                    disabled={navigationBusy || episodeReadySaving}
                    onClick={() => void handleToggleEpisodeReady()}
                    startIcon={
                      episodeReadySaving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : session.reviewReadyAt ? (
                        <CheckCircleIcon />
                      ) : undefined
                    }
                  >
                    {session.reviewReadyAt ? "Mark pending" : "Mark ready"}
                    <ButtonShortcutLabel shortcut={formatModShortcut("↩")} />
                  </Button>
                )}
                {episodeNav?.hasNext && (
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<ChevronRightIcon />}
                    onClick={() => void goToEpisode(1)}
                    disabled={navigationBusy}
                  >
                    Next episode
                    <ButtonShortcutLabel
                      shortcut={IS_APPLE_PLATFORM ? "⌘⌥→" : "Ctrl+Alt+→"}
                    />
                  </Button>
                )}
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: 1 },
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                p: 4,
                textAlign: "center",
                borderRight: { md: 1 },
                borderBottom: { xs: 1, md: 0 },
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">No mentions to review</Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={420}>
                Open the episode on YouTube to check for brand mentions. Add any
                the AI missed, or mark the episode ready if there are none.
              </Typography>
              {onAddMissedMention && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddMissedOpen(true)}
                >
                  Add mention
                </Button>
              )}
            </Box>
            <Box
              sx={{
                flex: { xs: 1, md: "0 0 42%" },
                maxWidth: { md: 520 },
                minWidth: { md: 320 },
                minHeight: { xs: 280, md: 0 },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <MentionTranscriptPanel
                contentId={session.contentId}
                youtubeBar={youtubeBar}
                onSeekToTime={youtubeWatchUrl ? seekYoutubeToTime : undefined}
              />
            </Box>
          </DialogContent>
        </>
      ) : null}

      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          if (rejectInProgress) return;
          setRejectDialogOpen(false);
          setRejectReason("");
          setRejectContext("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {rejectScope === "selected" && rejectSelectedCount > 1
            ? `Mark ${rejectSelectedCount} mentions as not a mention`
            : "Mark as not a mention"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 0.5 }}>
            Pick a rejection reason.{" "}
            {rejectScope === "selected" && rejectSelectedCount > 1
              ? `All ${rejectSelectedCount} selected mentions`
              : "The mention"}{" "}
            will be kept as rejected for metrics and training data. Add context only
            if you need extra detail.
          </Typography>
          <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
            {REJECT_MENTION_REASON_PRESETS.map((preset) => (
              <Chip
                key={preset}
                label={preset}
                size="small"
                onClick={() => setRejectReason(preset)}
                color={rejectReason === preset ? "warning" : "default"}
                variant={rejectReason === preset ? "filled" : "outlined"}
                sx={{ cursor: "pointer" }}
                disabled={rejectInProgress}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Context (optional)"
            value={rejectContext}
            onChange={(e) => setRejectContext(e.target.value)}
            disabled={rejectInProgress}
            placeholder='e.g. "mundo in todo el mundo" or duped of <mentionId>'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectReason("");
              setRejectContext("");
            }}
            disabled={rejectInProgress}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => void handleConfirmReject()}
            disabled={rejectInProgress || !rejectReason.trim()}
          >
            {rejectInProgress
              ? "Rejecting…"
              : rejectScope === "selected" && rejectSelectedCount > 1
                ? `Reject ${rejectSelectedCount}`
                : "Reject"}
            {!rejectInProgress && (
              <ButtonShortcutLabel shortcut={formatModEnterShortcut()} />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteInProgress && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete mention</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Permanently delete{" "}
            <strong>{currentMention?.brandName ?? "this mention"}</strong> (
            {currentMention
              ? `${formatMentionTime(currentMention.startTime)} – ${formatMentionTime(currentMention.endTime)}`
              : ""}
            )? This cannot be undone.
          </Typography>
          {currentMention && !currentMention.review?.humanAdded && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              To keep the mention for training metrics, use{" "}
              <strong>Not a mention</strong> instead.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteInProgress}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDeleteCurrentMention()}
            disabled={deleteInProgress}
          >
            {deleteInProgress ? "Deleting…" : "Delete"}
            {!deleteInProgress && (
              <ButtonShortcutLabel shortcut={formatModEnterShortcut()} />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {onAddMissedMention && (
        <AddMissedMentionDialog
          open={addMissedOpen}
          onClose={() => !addMissedSaving && setAddMissedOpen(false)}
          sourceMention={currentMention}
          saving={addMissedSaving}
          onSave={async (edits) => {
            setAddMissedSaving(true);
            try {
              const created = await onAddMissedMention(currentMention, edits);
              if (created.length === 0) return;
              const nextMentions = [...mentions, ...created];
              onMentionsChange(nextMentions);
              currentMentionIdRef.current = created[0]._id;
              const firstCreatedIndex = nextMentions.findIndex(
                (m) => m._id === created[0]._id,
              );
              setCurrentIndex(
                firstCreatedIndex >= 0
                  ? firstCreatedIndex
                  : nextMentions.length - created.length,
              );
              setAddMissedOpen(false);
            } finally {
              setAddMissedSaving(false);
            }
          }}
        />
      )}
    </Dialog>
  );
};

type SelectedMissedBrand = { brandName: string; brandId?: string };

type AddMissedMentionDialogProps = {
  open: boolean;
  onClose: () => void;
  sourceMention: BrandMention | null;
  saving: boolean;
  onSave: (edits: {
    brands: SelectedMissedBrand[];
    startTime: number;
    endTime: number;
    mentionType: string;
    placementType: PlacementType;
    visualCue?: VisualCue | null;
  }) => Promise<void>;
};

const DEFAULT_NEW_MENTION_DURATION_SEC = 15;

const AddMissedMentionDialog: React.FC<AddMissedMentionDialogProps> = ({
  open,
  onClose,
  sourceMention,
  saving,
  onSave,
}) => {
  const [selectedBrands, setSelectedBrands] = useState<SelectedMissedBrand[]>(
    [],
  );
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [startTime, setStartTime] = useState(sourceMention?.startTime ?? 0);
  const [endTime, setEndTime] = useState(
    sourceMention?.endTime ?? DEFAULT_NEW_MENTION_DURATION_SEC,
  );
  const [mentionType, setMentionType] = useState(
    sourceMention?.classification || "paid",
  );
  const [placementType, setPlacementType] = useState<PlacementType>(
    sourceMention?.placementType ?? "content",
  );
  const [visualCue, setVisualCue] = useState<VisualCue | "">(
    sourceMention?.visualCue ?? "",
  );
  const [brandSearchFocused, setBrandSearchFocused] = useState(false);
  const [highlightedBrandIndex, setHighlightedBrandIndex] = useState(-1);
  const brandSearchBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const brandSuggestionListRef = useRef<HTMLUListElement | null>(null);

  const debouncedSearchQuery = useDebounce(
    brandSearchQuery,
    BRAND_SEARCH_DEBOUNCE_MS,
  );

  const { data: brandsData, isFetching: brandsFetching } = useQuery({
    queryKey: ["brands", debouncedSearchQuery || "all"],
    queryFn: () =>
      apiService.getBrands(1, 100, debouncedSearchQuery || undefined),
    enabled: open,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const filteredBrands = brandsData?.brands || [];
  const trimmedBrandQuery = brandSearchQuery.trim();
  const rankedBrands = useMemo(
    () => sortBrandsForSearch(filteredBrands, trimmedBrandQuery),
    [filteredBrands, trimmedBrandQuery],
  );
  const exactBrandNameMatch = useMemo(() => {
    const query = trimmedBrandQuery.toLowerCase();
    if (!query) return false;
    return filteredBrands.some((brand) => brand.name.toLowerCase() === query);
  }, [filteredBrands, trimmedBrandQuery]);

  const showBrandSuggestions =
    brandSearchFocused && trimmedBrandQuery.length >= 2;

  const isBrandAlreadySelected = useCallback(
    (brandName: string, brandId?: string) =>
      selectedBrands.some((b) =>
        brandId && b.brandId
          ? b.brandId === brandId
          : b.brandName.toLowerCase() === brandName.toLowerCase(),
      ),
    [selectedBrands],
  );

  const brandSuggestions = useMemo(() => {
    const items = rankedBrands.slice(0, 10).map((brand) => ({
      key: brand._id,
      brandName: brand.name,
      brandId: brand._id as string | undefined,
      disabled: isBrandAlreadySelected(brand.name, brand._id),
      isCreate: false,
    }));
    if (!exactBrandNameMatch && trimmedBrandQuery) {
      items.push({
        key: `use:${trimmedBrandQuery.toLowerCase()}`,
        brandName: trimmedBrandQuery,
        brandId: undefined,
        disabled: isBrandAlreadySelected(trimmedBrandQuery),
        isCreate: true,
      });
    }
    return items;
  }, [
    rankedBrands,
    exactBrandNameMatch,
    trimmedBrandQuery,
    isBrandAlreadySelected,
  ]);

  useEffect(() => {
    if (open) {
      setSelectedBrands([]);
      setBrandSearchQuery("");
      setStartTime(sourceMention?.startTime ?? 0);
      setEndTime(sourceMention?.endTime ?? DEFAULT_NEW_MENTION_DURATION_SEC);
      setMentionType(sourceMention?.classification || "paid");
      setPlacementType(sourceMention?.placementType ?? "content");
      setVisualCue(sourceMention?.visualCue ?? "");
      setBrandSearchFocused(false);
      setHighlightedBrandIndex(-1);
    }
  }, [open, sourceMention]);

  useEffect(() => {
    if (!showBrandSuggestions) {
      setHighlightedBrandIndex(-1);
      return;
    }
    setHighlightedBrandIndex((prev) => {
      if (prev >= 0 && prev < brandSuggestions.length && !brandSuggestions[prev].disabled) {
        return prev;
      }
      return brandSuggestions.findIndex((suggestion) => !suggestion.disabled);
    });
  }, [showBrandSuggestions, brandSuggestions]);

  useEffect(() => {
    if (highlightedBrandIndex < 0) return;
    const list = brandSuggestionListRef.current;
    if (!list) return;
    const item = list.querySelector<HTMLElement>(
      `[data-suggestion-index="${highlightedBrandIndex}"]`,
    );
    item?.scrollIntoView({ block: "nearest" });
  }, [highlightedBrandIndex]);

  useEffect(() => {
    return () => {
      if (brandSearchBlurTimeoutRef.current) {
        clearTimeout(brandSearchBlurTimeoutRef.current);
      }
    };
  }, []);

  const handleBrandSearchFocus = () => {
    if (brandSearchBlurTimeoutRef.current) {
      clearTimeout(brandSearchBlurTimeoutRef.current);
      brandSearchBlurTimeoutRef.current = null;
    }
    setBrandSearchFocused(true);
  };

  const handleBrandSearchBlur = () => {
    brandSearchBlurTimeoutRef.current = setTimeout(() => {
      setBrandSearchFocused(false);
    }, 150);
  };

  const addBrand = (brandName: string, brandId?: string) => {
    const name = brandName.trim();
    if (!name || isBrandAlreadySelected(name, brandId)) return;
    setSelectedBrands((prev) => [
      ...prev,
      { brandName: name, brandId: brandId || undefined },
    ]);
    // Clear the query but keep focused — the input stays focused (mousedown
    // preventDefault), so onFocus won't fire again for the next brand.
    setBrandSearchQuery("");
    setBrandSearchFocused(true);
    setHighlightedBrandIndex(-1);
  };

  const moveBrandHighlight = (direction: 1 | -1) => {
    const enabledIndexes = brandSuggestions
      .map((suggestion, index) => (suggestion.disabled ? -1 : index))
      .filter((index) => index >= 0);
    if (enabledIndexes.length === 0) return;
    setHighlightedBrandIndex((prev) => {
      const currentPos = enabledIndexes.indexOf(prev);
      if (currentPos === -1) {
        return direction === 1
          ? enabledIndexes[0]
          : enabledIndexes[enabledIndexes.length - 1];
      }
      const nextPos =
        (currentPos + direction + enabledIndexes.length) % enabledIndexes.length;
      return enabledIndexes[nextPos];
    });
  };

  const handleBrandSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Escape" && showBrandSuggestions) {
      event.preventDefault();
      event.stopPropagation();
      setBrandSearchFocused(false);
      setHighlightedBrandIndex(-1);
      return;
    }

    if (!showBrandSuggestions || brandSuggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      moveBrandHighlight(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      moveBrandHighlight(-1);
      return;
    }
    if (event.key === "Enter") {
      const suggestion =
        highlightedBrandIndex >= 0
          ? brandSuggestions[highlightedBrandIndex]
          : undefined;
      if (!suggestion || suggestion.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      addBrand(suggestion.brandName, suggestion.brandId);
    }
  };

  const removeBrand = (index: number) => {
    setSelectedBrands((prev) => prev.filter((_, i) => i !== index));
  };

  const canSave =
    selectedBrands.length > 0 &&
    Boolean(mentionType) &&
    endTime > startTime &&
    !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {sourceMention ? "AI missed a mention" : "Add mention"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {sourceMention
            ? "Add one or more brands the AI missed at this timestamp. Times and classification start from the current mention."
            : "Add brand mentions the AI missed. Select brands, timestamps, and classification."}
        </Typography>
        <Stack spacing={2} sx={{ pt: 0 }}>
          <Box position="relative">
            <TextField
              autoFocus
              fullWidth
              label="Add brand"
              value={brandSearchQuery}
              onChange={(e) => setBrandSearchQuery(e.target.value)}
              onFocus={handleBrandSearchFocus}
              onBlur={handleBrandSearchBlur}
              onKeyDown={handleBrandSearchKeyDown}
              disabled={saving}
              helperText="Type to search and add brands"
              inputProps={{
                role: "combobox",
                "aria-expanded": showBrandSuggestions,
                "aria-autocomplete": "list",
                "aria-controls": "add-brand-suggestions",
                "aria-activedescendant":
                  highlightedBrandIndex >= 0
                    ? `add-brand-option-${highlightedBrandIndex}`
                    : undefined,
                autoComplete: "off",
              }}
              InputProps={{
                endAdornment: brandsFetching ? (
                  <CircularProgress size={20} />
                ) : undefined,
              }}
            />
            {showBrandSuggestions && (
              <Paper
                onMouseDown={(e) => e.preventDefault()}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  maxHeight: 240,
                  overflow: "auto",
                  mt: 1,
                }}
              >
                {brandsFetching && rankedBrands.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 2,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                <List
                  id="add-brand-suggestions"
                  dense
                  disablePadding
                  role="listbox"
                  ref={brandSuggestionListRef}
                >
                  {brandSuggestions.map((suggestion, index) => (
                    <ListItemButton
                      key={suggestion.key}
                      id={`add-brand-option-${index}`}
                      data-suggestion-index={index}
                      role="option"
                      aria-selected={index === highlightedBrandIndex}
                      selected={index === highlightedBrandIndex}
                      disabled={suggestion.disabled}
                      onClick={() =>
                        addBrand(suggestion.brandName, suggestion.brandId)
                      }
                      onMouseEnter={() => {
                        if (!suggestion.disabled) {
                          setHighlightedBrandIndex(index);
                        }
                      }}
                      sx={
                        suggestion.isCreate
                          ? {
                              borderTop: filteredBrands.length > 0 ? 1 : 0,
                              borderColor: "divider",
                            }
                          : undefined
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={suggestion.isCreate ? "medium" : "bold"}
                          >
                            {suggestion.isCreate
                              ? `Use "${suggestion.brandName}"`
                              : `${suggestion.brandName}${
                                  suggestion.disabled ? " (added)" : ""
                                }`}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
                )}
              </Paper>
            )}
          </Box>
          {selectedBrands.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {selectedBrands.map((brand, index) => (
                <Chip
                  key={`${brand.brandId || brand.brandName}-${index}`}
                  label={brand.brandName}
                  onDelete={saving ? undefined : () => removeBrand(index)}
                  size="small"
                />
              ))}
            </Stack>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DurationHmsInput
              label="Start"
              valueSeconds={startTime}
              onChange={setStartTime}
              disabled={saving}
            />
            <DurationHmsInput
              label="End"
              valueSeconds={endTime}
              onChange={setEndTime}
              disabled={saving}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth size="small" disabled={saving}>
              <InputLabel>Mention Type</InputLabel>
              <Select
                value={mentionType}
                label="Mention Type"
                onChange={(e) => setMentionType(e.target.value)}
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="organic">Organic</MenuItem>
                <MenuItem value="neutral">Neutral</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={saving}>
              <InputLabel>Placement</InputLabel>
              <Select
                value={placementType}
                label="Placement"
                onChange={(e) =>
                  setPlacementType(e.target.value as PlacementType)
                }
              >
                {PLACEMENT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <FormControl fullWidth size="small" disabled={saving}>
            <InputLabel>Visual Hint</InputLabel>
            <Select
              value={visualCue}
              label="Visual Hint"
              onChange={(e) => setVisualCue(e.target.value as VisualCue | "")}
            >
              {VISUAL_CUE_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() =>
            void onSave({
              brands: selectedBrands,
              startTime,
              endTime,
              mentionType,
              placementType,
              visualCue: visualCue || null,
            })
          }
        >
          {saving
            ? "Saving…"
            : selectedBrands.length > 1
              ? `Add ${selectedBrands.length} mentions`
              : "Add mention"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EpisodeMentionReviewDialog;
